import type { OperationVariables, TypedDocumentNode } from '@apollo/client';
import type {
  PreloadQueryFetchPolicy,
  PreloadQueryFunction,
} from '@apollo/client/react';
import type { Params } from 'react-router';

import type { Sport } from '#src/__generated__/globalTypes.ts';
import { preloadQuery as apiPreloadQuery } from '#src/contexts/graphql/Provider.tsx';
import { optimizeQuery } from '#src/lib/gql/optimizeQuery.ts';
import { stripPrefix } from '#src/lib/i18n.ts';
import { sportFromPath } from '#src/lib/routing/sportFromPath.ts';

import { shouldRevalidateFn } from './shouldRevalidateFn.ts';

export const queryLoader = <
  TParams extends Params,
  TData = any,
  TVariables extends OperationVariables = any,
>(
  query: TypedDocumentNode<TData, TVariables>,
  getVariables?: (
    options: TParams & {
      sport: Sport;
      searchParams: URLSearchParams;
      pathname: string;
    }
  ) => TVariables,
  loaderOptions?: {
    fetchPolicy?: PreloadQueryFetchPolicy;
    preventTransitionBeforeLoaded?: boolean;
    preloadQuery?: { current: PreloadQueryFunction };
  }
) => {
  const getVariablesFn = ({
    params,
    requestUrl,
  }: {
    params: TParams;
    requestUrl: URL;
  }) => {
    return getVariables?.({
      ...params,
      sport: sportFromPath(stripPrefix(requestUrl.pathname)),
      ...(params.sport ? { $sport: params.sport } : {}),
      pathname: requestUrl.pathname,
      searchParams: requestUrl.searchParams,
    });
  };

  const loaderFn = async ({
    params,
    request,
  }: {
    params: TParams;
    request: Request;
  }) => {
    const { fetchPolicy, preventTransitionBeforeLoaded, preloadQuery } =
      loaderOptions || {};
    const finalPreloadQuery = preloadQuery || apiPreloadQuery;

    if (!finalPreloadQuery.current) {
      throw new Error(
        'preloadQuery is not initialized. If this happens is a story, make sure graphql is part of the dependencies list.'
      );
    }
    const preloadedQueryRef = finalPreloadQuery.current(
      // optimize query only if default apiPreloadQuery is used
      preloadQuery ? query : optimizeQuery.current(query),
      // @ts-expect-error this apollo version types badly PreloadQueryFunction
      // so an {} object is needed even when the query asks for no variables
      {
        fetchPolicy:
          fetchPolicy ||
          // Default fetchPolicy is 'cache-first', override to 'cache-and-network'
          'cache-and-network',
        variables: getVariablesFn({ params, requestUrl: new URL(request.url) }),
      }
    );

    return preventTransitionBeforeLoaded
      ? finalPreloadQuery.current.toPromise(preloadedQueryRef)
      : preloadedQueryRef;
  };

  loaderFn.shouldRevalidate = shouldRevalidateFn(
    (params, requestUrl) =>
      getVariablesFn?.({
        params,
        requestUrl,
      }) || {}
  );

  return loaderFn;
};
